<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-lg-flex">
              <div>
                <h5 class="mb-0">Attribute Management</h5>
                <p class="text-sm mb-0">
                  Manage attribute groups and their values.
                </p>
              </div>
              <div class="ms-auto my-auto mt-lg-0 mt-4">
                <div class="ms-auto my-auto">
                  <button
                    class="btn bg-gradient-success btn-sm mb-0"
                    @click="onNewValue"
                    :disabled="!selectedGroupId"
                  >
                    +&nbsp; New Value
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pb-0">
            <div class="row px-4 mb-3">
              <div class="col-md-4">
                <label class="form-label">Select Attribute Group</label>
                <select
                  id="choices-attribute-group"
                  class="form-control"
                  v-model="selectedGroupId"
                >
                  <option value="" disabled>Choose a group</option>
                  <option
                    v-for="group in attributeGroups"
                    :key="group.id"
                    :value="group.id"
                  >
                    {{ group.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="table-responsive" v-if="renderTable">
              <table :key="tableKey" id="attribute-values-datatable" class="table table-flush">
                <thead class="thead-light">
                  <tr>
                    <th>ID</th>
                    <th>Value</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="val in attributeValues" :key="val.id">
                    <td class="text-sm">{{ val.id }}</td>
                    <td class="text-sm">{{ val.valor }}</td>
                    <td class="text-sm">{{ val.description || '-' }}</td>
                    <td class="text-sm">
                      <a
                        href="javascript:;"
                        class="mx-3"
                        data-bs-toggle="tooltip"
                        data-bs-original-title="Edit value"
                      >
                        <i 
                          class="material-icons text-secondary position-relative text-lg edit-btn cursor-pointer"
                          :data-id="val.id"
                        >edit</i>
                      </a>
                      <a
                        href="javascript:;"
                        data-bs-toggle="tooltip"
                        data-bs-original-title="Delete value"
                      >
                        <i 
                          class="material-icons text-secondary position-relative text-lg delete-btn cursor-pointer"
                          :data-id="val.id"
                        >delete</i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal
      :id="'attributeValueModal'"
      :title="isEditing ? 'Edit Attribute Value' : 'New Attribute Value'"
      dialogType="Dialog"
      :show="showModal"
      @close="showModal = false"
      @save="saveAttributeValue"
    >
      <div class="row mt-3">
        <div class="col-12">
          <material-input
            id="attr-value-name"
            v-model="currentValue.valor"
            label="Value"
            variant="dynamic"
            :isRequired="true"
          />
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <material-input
            id="attr-value-description"
            v-model="currentValue.description"
            label="Description"
            variant="dynamic"
          />
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { DataTable } from "simple-datatables";
import Choices from "choices.js";
import { mapActions, mapGetters } from "vuex";
import Modal from "../pages/components/Modal.vue";
import MaterialInput from "@/components/MaterialInput.vue";

export default {
  name: "Attributes",
  components: {
    Modal,
    MaterialInput,
  },
  data() {
    return {
      selectedGroupId: "",
      dataTable: null,
      groupChoices: null,
      showModal: false,
      isEditing: false,
      renderTable: true,
      tableKey: 0,
      currentValue: {
        id: null,
        valor: "",
        description: "",
        attributeGroupId: null,
      },
    };
  },
  computed: {
    ...mapGetters("product", {
      attributeGroups: "getAttributeGroups",
      attributeValues: "getBulkAttributeValues",
    }),
  },
  methods: {
    ...mapActions("product", [
      "getAttributeGroups",
      "getBulkAttributeValues",
      "clearBulkAttributeValues",
      "createAttributeValue",
      "updateAttributeValue",
      "deleteAttributeValue",
    ]),
    async onGroupChange() {
      if (this.selectedGroupId) {
        this.renderTable = false;
        await this.getBulkAttributeValues(this.selectedGroupId);
        this.tableKey++;
        this.renderTable = true;
        this.$nextTick(() => {
          this.initDataTable();
        });
      }
    },
    initDataTable() {
      if (this.dataTable) {
        this.dataTable.destroy();
        this.dataTable = null;
      }
      if (document.getElementById("attribute-values-datatable")) {
        this.dataTable = new DataTable("#attribute-values-datatable", {
          searchable: true,
          fixedHeight: false,
          perPageSelect: false,
        });

        // Event Delegation for Edit and Delete buttons
        this.dataTable.dom.addEventListener("click", (event) => {
          const target = event.target;
          if (target.classList.contains("edit-btn")) {
            const id = target.dataset.id;
            const val = this.attributeValues.find(v => v.id == id);
            if (val) this.onEditValue(val);
          } else if (target.classList.contains("delete-btn")) {
            const id = target.dataset.id;
            this.onDeleteValue(id);
          }
        });
      }
    },
    initChoices() {
      if (document.getElementById("choices-attribute-group")) {
        const element = document.getElementById("choices-attribute-group");
        this.groupChoices = new Choices(element, {
          searchEnabled: true,
          allowHTML: true,
          shouldSort: false,
        });
        element.addEventListener("change", (event) => {
          this.selectedGroupId = event.target.value;
          this.onGroupChange();
        });
      }
    },
    onNewValue() {
      this.isEditing = false;
      this.currentValue = {
        id: null,
        valor: "",
        description: "",
        attributeGroupId: this.selectedGroupId,
      };
      this.showModal = true;
    },
    onEditValue(val) {
      this.isEditing = true;
      this.currentValue = { ...val };
      this.showModal = true;
    },
    async onDeleteValue(id) {
      if (confirm("Are you sure you want to delete this attribute value?")) {
        try {
          this.renderTable = false;
          await this.deleteAttributeValue({
            id,
            attributeGroupId: this.selectedGroupId,
          });
          this.tableKey++;
          this.renderTable = true;
          this.$swal.fire("Deleted!", "Attribute value has been deleted.", "success");
          this.$nextTick(() => {
            this.initDataTable();
          });
        } catch (error) {
          this.renderTable = true;
          this.$swal.fire("Error!", "Could not delete attribute value.", "error");
        }
      }
    },
    async saveAttributeValue() {
      try {
        this.renderTable = false;
        if (this.isEditing) {
          await this.updateAttributeValue({
            id: this.currentValue.id,
            attributeValueDTO: this.currentValue,
          });
          this.$swal.fire("Success!", "Attribute value updated.", "success");
        } else {
          await this.createAttributeValue(this.currentValue);
          this.$swal.fire("Success!", "Attribute value created.", "success");
        }
        this.tableKey++;
        this.renderTable = true;
        this.showModal = false;
        this.$nextTick(() => {
          this.initDataTable();
        });
      } catch (error) {
        this.renderTable = true;
        this.$swal.fire("Error!", "Could not save attribute value.", "error");
      }
    },
  },
  async mounted() {
    await this.getAttributeGroups();
    this.initChoices();
  },
  beforeUnmount() {
    if (this.dataTable) {
      this.dataTable.destroy();
    }
    if (this.groupChoices) {
      this.groupChoices.destroy();
    }
    this.clearBulkAttributeValues();
  },
};
</script>
